import { useCallback, useEffect, useRef } from 'react'
import {
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faXmarkCircle,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { clsx } from 'clsx'

import type { ToastProps } from './Toast.types'
import type { ToastVariant } from './ToastProvider.types'

const VARIANT_ICONS: Record<ToastVariant, typeof faCircleCheck> = {
  error: faXmarkCircle,
  info: faCircleInfo,
  success: faCircleCheck,
  warning: faCircleExclamation,
}

export const Toast = (props: ToastProps) => {
  const {
    content,
    variant = 'success',
    autoClose,
    hideCloseButton,
    onClose,
  } = props
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const remainingRef = useRef(autoClose ? autoClose * 1000 : 0)
  const startTimeRef = useRef(0)

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const startTimer = useCallback(() => {
    if (!autoClose || remainingRef.current <= 0) {
      return
    }

    startTimeRef.current = Date.now()
    timerRef.current = setTimeout(onClose, remainingRef.current)
  }, [autoClose, onClose])

  const pauseTimer = useCallback(() => {
    if (!timerRef.current) {
      return
    }

    clearTimer()
    remainingRef.current -= Date.now() - startTimeRef.current
  }, [clearTimer])

  useEffect(() => {
    startTimer()

    return clearTimer
  }, [startTimer, clearTimer])

  useEffect(() => {
    if (!autoClose) {
      return
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        pauseTimer()
      } else {
        startTimer()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [autoClose, pauseTimer, startTimer])

  const isUrgent = variant === 'error' || variant === 'warning'

  return (
    <div
      aria-live={isUrgent ? 'assertive' : 'polite'}
      className={clsx('Toast', `Toast--${variant}`, {
        'Toast--autoClose': autoClose,
      })}
      onMouseEnter={autoClose ? pauseTimer : undefined}
      onMouseLeave={autoClose ? startTimer : undefined}
      role={isUrgent ? 'alert' : 'status'}
    >
      <span className="Toast-icon">
        <FontAwesomeIcon icon={VARIANT_ICONS[variant]} />
      </span>

      <span className="Toast-content">{content}</span>

      {!hideCloseButton && (
        <button
          aria-label="Close"
          className="Toast-close"
          onClick={onClose}
          type="button"
        >
          &#x2715;
        </button>
      )}

      {!!autoClose && (
        <div
          className="Toast-progressTrack"
          style={{ animationDuration: `${autoClose}s` }}
        />
      )}
    </div>
  )
}
